/* eslint-disable @typescript-eslint/no-magic-numbers */
import type { Comparison } from '@sembo-private/comparisons';
import {
  booleanComparer,
  compare,
  localeStringComparer,
  on,
  onLocaleString,
  onNumber,
  reversed,
} from '@sembo-private/comparisons';

import type { PriceSegment } from '@apps/main/new-desktop/pages/tripOverview/models';

import { IngredientType } from '@libs/trip-planner-interfaces';
import type {
  CancellationPolicy,
  Money,
  ItineraryIngredientResponse,
  HotelBookableAndLockableOption,
} from '@libs/trip-planner-interfaces';
import { SemboDate } from '@libs/sembo-dates';
import { isTransportIngredientResponse } from '@libs/search-service-smart-search';

import { isTransportIngredientType } from '../recipe/ingredients/typeGuards';

export const cheapestPriceComparer = compare<{
  totalPrice: Money;
  hotelCode: string;
}>(
  onNumber((item) => item.totalPrice.amount),
  propertyStringComparer((item) => item.hotelCode)
);

export const bookableOptionComparer = compare<HotelBookableAndLockableOption>(
  reversed((a, b) => booleanComparer(a.isLocked, b.isLocked)),
  reversed((a, b) => booleanComparer(a.isFromMeta, b.isFromMeta)),
  onNumber((a) => a.totalPrice.amount)
);

export const popularityHotelComparer = compare<{
  popularityScore: number;
  hotelCode: string;
}>(
  reversed(onNumber((item) => item.popularityScore)),
  propertyStringComparer((item) => item.hotelCode)
);

export const popularityAirportComparer = compare<{
  airport: { popularity: number } | null;
}>(reversed(onNumber((item) => item.airport?.popularity ?? 0)));

export const ingredientDateComparer = compare<ItineraryIngredientResponse>(
  on(
    (ingredient: ItineraryIngredientResponse) => ingredient,
    (a, b) => {
      const semboDateA = new SemboDate(a.date);
      const semboDateB = new SemboDate(b.date);

      if (semboDateA.isSameDay(b.date)) {
        return isTransportIngredientResponse(a) ? -1 : 1;
      }
      return semboDateA.isAfter(semboDateB.startOf('day').get()) ? 1 : -1;
    }
  )
);

export const stringComparer = compare(on(JSON.stringify, localeStringComparer));

export function cnxCompare(
  cnxA: CancellationPolicy,
  cnxB: CancellationPolicy
): number {
  return dateCompare(cnxA.fromDate, cnxB.fromDate);
}

export function propertyStringComparer<T>(
  propertySelector: (item: T) => string
): Comparison<T> {
  return compare<T>(onLocaleString(propertySelector));
}

// TODO: Unify with @sembo-private/comparisons when moving SemboDate to external repo
export function dateCompare(
  dateA: Date | string,
  dateB: Date | string
): number {
  const semboDateA = new SemboDate(dateA);
  const semboDateB = new SemboDate(dateB);
  if (semboDateA.isSame(semboDateB.get())) {
    return 0;
  }
  return semboDateA.isAfter(semboDateB.get()) ? 1 : -1;
}

export const tripSummaryPriceSegmentsComparer = compare<PriceSegment>(
  (a, b) => {
    const getOrder = (ingredientType): number => {
      if (ingredientType.includes(IngredientType.Hotel)) {
        return 1;
      }
      if (isTransportIngredientType(ingredientType)) {
        return 2;
      }
      if (ingredientType.includes(IngredientType.Activity)) {
        return 3;
      }
      return 4;
    };

    const orderA = getOrder(a.ingredientType);
    const orderB = getOrder(b.ingredientType);

    if (orderA !== orderB) {
      return orderA - orderB;
    }
    // If they are of the same type (transport), sort alphabetically by locale label
    return a.label.localeCompare(b.label);
  }
);
